import type {
    GeneratedInsight,
    Status,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { isUndefined } from "lodash";
import React, { useMemo } from "react";
import { useStore } from "zustand";

import { useSetInsightDetailComponents } from "../../../../../hooks/insightDetail/useSetInsightDetailComponents";
import { insightDetailComponentStore } from "../../../../../store/insightDetailComponent";
import { InsightLinkageStore } from "../../../../../store/insightLinkage";
import { workbenchStore } from "../../../../../store/workbench";
import type { CardDetail } from "../../../../../types/card";
import type { DownloadAsset } from "../../../../../types/common";
import { InsightPreviewFileFormats } from "../../../../../types/insightPreviewFileFormats";
import type {
    InsightDetailOption,
    InsightDetailSelectionProps,
} from "../../../../../utils/insightDetail.utils";
import { InsightDetailType } from "../../../../../utils/insightDetail.utils";
import { PdfSelectionViewer } from "../../../../common/PdfSelectionViewer";
import {
    HeaderComponentBuilder,
    Header,
    HeaderTitle,
    HeaderCardDetails,
    BodyComponentBuilder,
    FooterComponentBuilder,
    FooterDownloadButton,
} from "../../../../common/insightDetail.components";
import { ToolbarButtonGroup } from "../../../../common/styled.components";
import { RelevantInsightButton } from "../../../../relevantInsight/RelevantInsightButton";
import { WorkbenchActions } from "../../insightDetails/WorkbenchActions";

import { EditInputsButton } from "./PackagedInsightsDetails.Actions";

export interface PackagedInsightDetails {
    details: CardDetail[];
    title: string;
    executionId: string;
    identifier: string;
    insightType: InsightType;
    sourceInsightType: InsightType;
    description?: string;
    previewAsset?: DownloadAsset;
    downloadAsset?: DownloadAsset;
    previewLoading: boolean;
    downloadLoading: boolean;
    insight: GeneratedInsight;
    requesterAlias: string;
    advertiserId: string;
    aiEnhancedStatus?: Status;
    hasRelevantInsightsDeck?: boolean;
    reset: () => void;
}

export const useSetPackagedInsightDetails = (
    props: PackagedInsightDetails,
): (() => void) => {
    const {
        executionId,
        identifier,
        insightType,
        sourceInsightType,
        insight,
        details,
        title,
        description = "",
        previewAsset,
        downloadAsset,
        previewLoading,
        downloadLoading,
        requesterAlias,
        advertiserId,
        aiEnhancedStatus,
        hasRelevantInsightsDeck,
        reset,
    } = props;

    const [resetStore] = useStore(insightDetailComponentStore, (state) => [
        state.reset,
    ]);

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [setInsightLinkageOpen, setInsightLinkageMetadata] = useStore(
        InsightLinkageStore,
        (state) => [state.setOpen, state.setInsightLinkageMetadata],
    );

    const [numPages, setNumPages] = React.useState<number>(0);

    const handleMount = (props: InsightDetailSelectionProps): void => {
        props.setSelectedSlides(getSlideIndexes(identifier));
    };

    const handleClose = () => {
        setNumPages(0);
        resetStore();
        reset();
    };

    const handleDownload = () => {
        setInsightLinkageMetadata({
            title,
            content: downloadAsset?.content,
            identifier,
            downloadUrl: downloadAsset?.downloadUrl,
            insightType,
            description,
        });
        setInsightLinkageOpen(true);
        handleClose();
    };

    const PackagedInsightHeaderOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightHeader: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <HeaderComponentBuilder
                loading={previewLoading}
                headerComponents={[
                    <Header
                        numPages={numPages}
                        identifier={identifier}
                        insightType={insightType}
                    />,
                    <HeaderTitle title={title} />,
                    <HeaderCardDetails
                        details={details}
                        selectedSlides={props.selectedSlides}
                        setSelectedSlides={props.setSelectedSlides}
                        numPages={numPages}
                        previewAsset={previewAsset}
                        identifier={identifier}
                        insightType={insightType}
                        previewFileFormat={InsightPreviewFileFormats.PDF}
                        downloadAsset={downloadAsset}
                        isCompliant={true}
                        outputFileFormats={[]}
                        aiSummaryOverview={insight.aiSummaryOverview}
                        aiSummaryExecutive={insight.aiSummaryExecutive}
                    />,
                    hasRelevantInsightsDeck ? (
                        <RelevantInsightButton
                            sourceInsightType={sourceInsightType}
                            sourceIdentifier={executionId}
                            requesterAlias={requesterAlias}
                            advertiserId={advertiserId}
                        />
                    ) : (
                        <></>
                    ),
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightHeader,
        };
    }, [
        identifier,
        numPages,
        details,
        previewAsset,
        downloadAsset,
        previewLoading,
        executionId,
    ]);

    const PackagedInsightBodyOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightBody: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <BodyComponentBuilder
                loading={previewLoading}
                bodyComponents={[
                    <PdfSelectionViewer
                        numPages={numPages}
                        setNumPages={setNumPages}
                        url={previewAsset ? previewAsset.downloadUrl! : ""}
                        insightType={insightType}
                        identifier={identifier}
                        selectedSlides={props.selectedSlides}
                        setSelectedSlides={props.setSelectedSlides}
                        isCompliant={true}
                    />,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightBody,
        };
    }, [previewAsset, numPages, identifier, previewLoading]);

    const insightTypeSubType =
        details.find((detail) => detail.label === "Template")?.value ?? "";

    const PackagedInsightFooterOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightFooter: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <FooterComponentBuilder
                loading={downloadLoading}
                handleClose={handleClose}
                identifier={identifier}
                insightType={insightType}
                footerComponents={[
                    <ToolbarButtonGroup>
                        <FooterDownloadButton
                            handleDownload={handleDownload}
                            downloadAsset={downloadAsset}
                            identifier={identifier}
                            insightType={insightType}
                            title={title}
                        />
                        <EditInputsButton insight={insight} />
                    </ToolbarButtonGroup>,
                    <WorkbenchActions
                        title={title}
                        identifier={identifier}
                        selectedSlides={props.selectedSlides}
                        setSelectedSlides={props.setSelectedSlides}
                        outputFileFormats={[]}
                        numPages={numPages}
                        downloadAsset={downloadAsset}
                        previewAsset={previewAsset}
                        insightType={insightType}
                        insightTypeSubType={insightTypeSubType}
                        isCompliant={true}
                    />,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightFooter,
        };
    }, [
        identifier,
        downloadAsset,
        previewAsset,
        numPages,
        downloadLoading,
        description,
    ]);

    const previewReady: boolean = useMemo(() => {
        return !isUndefined(previewAsset) || previewLoading;
    }, [previewAsset, previewLoading]);

    const downloadReady: boolean = useMemo(() => {
        return !isUndefined(downloadAsset) || downloadLoading;
    }, [downloadAsset, downloadLoading]);

    return useSetInsightDetailComponents({
        identifier,
        familyName: "",
        InsightHeaderOption: PackagedInsightHeaderOption,
        InsightBodyOption: PackagedInsightBodyOption,
        InsightFooterOption: PackagedInsightFooterOption,
        headerReady: previewReady,
        bodyReady: previewReady,
        footerReady: downloadReady,
        handleMount,
        handleClose,
    });
};
